import {
	Checkbox,
	FormControlLabel,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import * as React from 'react'
import { StyledTableCell } from '../../core/StyledTableCell'
import { DirectReportAttendance } from '../../../types/Employee/Attendance'
import { getDirectReportsAttendance, signInWorkersBulk } from '../../../services/attendanceService'
import { getProjectAreas } from '../../../services/projectService'
import { WorkArea } from '../../../types/Project/WorkArea'
import { getProjectMapImage } from '../../../services/attachmentService'
import ConfirmDialog from '../../core/ConfirmDialog'
import { AttendanceRow } from './AttendanceRow'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { useConsentContext } from './ConsentContext'
import { showSuccessToast } from '../../../services/CommonServices/alertServices'
import { useIndexedDB } from '../../hooks/useIndexedDB'
import {
	AttendanceLayoutVariant,
	attendanceDialogBodySx,
	attendancePageBodySx,
	attendanceSignOnTableContainerSx,
} from './attendanceLayout'
import { AttendanceActions, AttendanceBody, AttendanceShell } from './AttendanceShell'

type SignOnDialogProps = {
	handleClose?: () => void
	open: boolean
	supervisorId?: number | undefined
	department: string
	variant?: AttendanceLayoutVariant
}

const emptyAttendance: DirectReportAttendance = {
	areaId: 0,
	areaName: null,
	date: null,
	fullName: '',
	signInAt: null,
	signOutAt: null,
	status: null,
	workerId: 0,
}

export function SignOnDialog({
	handleClose,
	open,
	supervisorId,
	department,
	variant = 'dialog',
}: SignOnDialogProps) {
	const [selectedLaborers, setSelectedLaborers] = React.useState<number[]>([])
	const [attenadances, setAttendances] = React.useState<DirectReportAttendance[]>([])
	const [loading, setLoading] = React.useState<boolean>(false)
	const [workAreas, setWorkAreas] = React.useState<WorkArea[]>([])
	const [mapUrl, setMapUrl] = React.useState<string>()
	const [openConfirmOverride, setOpenConfirmOverride] = React.useState(false)
	const [confirmOverride, setConfirmOverride] = React.useState(1)
	const [hasSetBoth, setHasSetBoth] = React.useState(false)
	const [selectRowAttendance, setSelectRowAttendance] =
		React.useState<DirectReportAttendance>(emptyAttendance)
	const originalAttendances = React.useRef<DirectReportAttendance[]>()
	const { generateConsentForms, getSignature, loadConsentTexts, clearSignatures } =
		useConsentContext()
	const db = useIndexedDB()
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const isDaysOffEnabled = isFeatureEnabled(3)
	const isWorkAreaEnabled = isFeatureEnabled(5)
	const isConsentEnabled = isFeatureEnabled(6)
	const loadProjectMap = async () => {
		try {
			const mapUrl = await getProjectMapImage()
			setMapUrl(mapUrl)
		} catch (error) {
			setMapUrl(undefined)
		}
	}

	const loadAreas = async () => {
		const areas = await getProjectAreas()
		setWorkAreas(areas)
	}

	const loadDirectReportsAttendance = async () => {
		setLoading(true)
		const data = await getDirectReportsAttendance(department, supervisorId, false, db)
		setAttendances(data)
		originalAttendances.current = data
		setLoading(false)
	}

	const handleStatusChange = (workerId: number, newStatus: string) => {
		if (selectedLaborers.includes(workerId)) {
			setSelectedLaborers([workerId])
		}

		setAttendances((prev) =>
			prev.map((worker) =>
				worker.workerId === workerId
					? {
							...worker,
							status: worker.status === newStatus ? null : newStatus,
							statusError: false,
					  }
					: worker,
			),
		)
	}

	const handleLocationChange = (workerId: number, area: WorkArea) => {
		setAttendances((prev) =>
			prev.map((worker) =>
				worker.workerId === workerId
					? {
							...worker,
							areaId: area.id,
							areaName: area.name,
							areaError: false,
					  }
					: worker,
			),
		)
	}

	const handleBulkStatusChange = (newStatus: string) => {
		setSelectRowAttendance((prev) => ({
			...prev,
			status: prev.status === newStatus ? null : newStatus,
		}))
		setAttendances((prev) =>
			prev.map((worker) =>
				selectedLaborers.includes(worker.workerId)
					? {
							...worker,
							status: selectRowAttendance.status === newStatus ? null : newStatus,
							statusError: false,
					  }
					: worker,
			),
		)
		// Check if we've set both status and location for the bulk selection
		if (
			(newStatus && selectRowAttendance.areaId !== 0) ||
			((newStatus === 'Absent' || newStatus === 'Days Off') && selectRowAttendance.status === null)
		) {
			setHasSetBoth(true)
		}
	}

	const handleBulkLocationChange = (area: WorkArea) => {
		setSelectRowAttendance((prev) => ({
			...prev,
			areaId: area.id,
			areaName: area.name,
		}))
		setAttendances((prev) =>
			prev.map((worker) =>
				selectedLaborers.includes(worker.workerId)
					? {
							...worker,
							areaId: area.id,
							areaName: area.name,
							areaError: false,
					  }
					: worker,
			),
		)
		// Check if we've set both status and location for the bulk selection
		if (area.id !== 0 && selectRowAttendance.status) {
			setHasSetBoth(true)
		}
	}

	const handleLaborerSelected = (laborer: DirectReportAttendance, checked: boolean) => {
		if (checked) {
			setSelectedLaborers((prev) => [...prev, laborer.workerId])
			setHasSetBoth(false)
			setSelectRowAttendance(emptyAttendance)
		} else {
			setSelectedLaborers((prev) => prev.filter((l) => l !== laborer.workerId))
			if (selectedLaborers.length <= 1) {
				setSelectRowAttendance(emptyAttendance)
			}
		}
	}

	// Add useEffect to clear selection when both are set
	React.useEffect(() => {
		if (hasSetBoth) {
			setSelectedLaborers([])
			setHasSetBoth(false)
			setSelectRowAttendance(emptyAttendance)
		}
	}, [hasSetBoth])

	async function handleConfirm() {
		const changedAttendances = attenadances.filter((a) => {
			const original = originalAttendances.current?.find((o) => o.workerId === a.workerId)
			if (!original) return false
			return a.status !== original.status || a.areaId !== original.areaId
		})

		if (changedAttendances.length === 0) {
			handleClose?.()
			return
		}

		// Validate all changes
		let invalid = false
		for (const attendance of changedAttendances) {
			const invalidArea =
				isWorkAreaEnabled && attendance.areaId === 0 && attendance.status === 'Present'
			const invalidStatus = !attendance.status

			const invalidConsent =
				isConsentEnabled && attendance.status === 'Present' && !getSignature(attendance.workerId)

			if (invalidArea || invalidStatus || invalidConsent) {
				setAttendances((prev) =>
					prev.map((worker) =>
						worker.workerId === attendance.workerId
							? {
									...worker,
									areaError: invalidArea,
									statusError: invalidStatus,
									consentError: invalidConsent,
							  }
							: worker,
					),
				)
				invalid = true
			}
		}

		if (invalid) return

		// Generate consent forms for changed attendances if consent is enabled
		const forms = isConsentEnabled
			? generateConsentForms(changedAttendances.map((a) => a.workerId))
			: []

		setLoading(true)
		try {
			await signInWorkersBulk(changedAttendances, forms, db)
			await loadDirectReportsAttendance()
			showSuccessToast(`Sign on records for ${changedAttendances.length} workers submitted.`, true)
			setSelectedLaborers([])
			setConfirmOverride(1)
		} finally {
			setLoading(false)
		}
	}

	React.useEffect(() => {
		if (open) {
			const fetchAll = async () => {
				await Promise.all([
					loadDirectReportsAttendance(),
					isWorkAreaEnabled && loadAreas(),
					isWorkAreaEnabled && loadProjectMap(),
					isConsentEnabled && loadConsentTexts(),
				])
			}
			fetchAll()
		}
		return () => {
			clearSignatures()
		}
	}, [open])

	React.useEffect(() => {
		if (confirmOverride === 2 || confirmOverride === 3) {
			handleConfirm()
		}
	}, [confirmOverride])

	const getSelectedAttendance = () => {
		if (selectedLaborers.length === 0) return emptyAttendance
		if (selectedLaborers.length > 1) return selectRowAttendance
		const firstSelected = attenadances.find((a) => a.workerId === selectedLaborers[0])
		return firstSelected || emptyAttendance
	}

	function handleConsentChange(workerId: number): void {
		setAttendances((prev) =>
			prev.map((worker) =>
				worker.workerId === workerId ? { ...worker, consentError: false } : worker,
			),
		)
	}

	return (
		<AttendanceShell variant={variant}>
			<AttendanceBody
				variant={variant}
				sx={variant === 'dialog' ? attendanceDialogBodySx : attendancePageBodySx}
			>
				<TableContainer sx={attendanceSignOnTableContainerSx(variant)}>
					<Table sx={{ width: '100%' }} stickyHeader>
						<TableHead>
							{selectedLaborers.length > 0 ? (
								<AttendanceRow
									onStatusChange={handleBulkStatusChange}
									onLocationChange={handleBulkLocationChange}
									mapUrl={mapUrl}
									attendance={getSelectedAttendance()}
									workAreas={workAreas}
									nameColumn={`${selectedLaborers.length} rows selected`}
									selectRow={true}
									selectAllCheckBox={
										<FormControlLabel
											sx={{ mr: 0 }}
											control={
												<Checkbox
													checked={
														selectedLaborers.length === attenadances?.length &&
														attenadances.length > 0
													}
													onChange={(_, checked) =>
														checked && attenadances
															? setSelectedLaborers(attenadances.map((a) => a.workerId))
															: setSelectedLaborers([])
													}
												/>
											}
											label='Select All'
										/>
									}
								/>
							) : (
								<TableRow>
									<StyledTableCell align='left' sx={{ pr: 0, width: '8rem' }}>
										<FormControlLabel
											sx={{ mr: 0 }}
											control={
												<Checkbox
													checked={
														selectedLaborers.length === attenadances?.length &&
														attenadances.length > 0
													}
													onChange={(_, checked) =>
														checked && attenadances
															? setSelectedLaborers(attenadances.map((a) => a.workerId))
															: setSelectedLaborers([])
													}
												/>
											}
											label='Select All'
										/>
									</StyledTableCell>
									<StyledTableCell align='left' sx={{ px: 0 }}>
										Name
									</StyledTableCell>
									<StyledTableCell align='left'></StyledTableCell>
									<StyledTableCell align='left'></StyledTableCell>
									{isDaysOffEnabled && <StyledTableCell align='left'></StyledTableCell>}
									{isWorkAreaEnabled && (
										<StyledTableCell align='center' sx={{ pl: 0, width: '20rem' }}>
											Work Area
										</StyledTableCell>
									)}
									{isConsentEnabled && <StyledTableCell align='center'>Consent Form</StyledTableCell>}
								</TableRow>
							)}
						</TableHead>
						<TableBody>
							{attenadances ? (
								attenadances.map((laborer) => (
									<AttendanceRow
										key={laborer.workerId}
										onStatusChange={(status) => handleStatusChange(laborer.workerId, status)}
										onLocationChange={(area) => handleLocationChange(laborer.workerId, area)}
										onConsentChange={() => handleConsentChange(laborer.workerId)}
										mapUrl={mapUrl}
										attendance={laborer}
										workAreas={workAreas}
										nameColumn={laborer.fullName}
										selectRow={false}
										handleWorkerSelected={handleLaborerSelected}
										selectedWorkers={selectedLaborers}
									/>
								))
							) : (
								<TableRow>
									<TableCell align='center' colSpan={6}>
										All your workers have been signed in for the day.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</AttendanceBody>
			<AttendanceActions
				variant={variant}
				loading={loading}
				onCancel={() => handleClose?.()}
				onConfirm={handleConfirm}
			/>

			<ConfirmDialog
				title={'Confirm Sign on'}
				confirmButtonText={'Confirm'}
				visible={openConfirmOverride}
				onConfirm={function (): void {
					setConfirmOverride(2)
					setOpenConfirmOverride(false)
				}}
				cancelButtonText='Keep Individual'
				onCancel={() => {
					setConfirmOverride(3)
					setOpenConfirmOverride(false)
				}}
				onClickOut={() => {
					setOpenConfirmOverride(false)
				}}
			>
				You chose different options for{' '}
				{attenadances
					.filter((a) => {
						const original = originalAttendances.current?.find((o) => o.workerId === a.workerId)
						if (!original) return false
						return a.status !== original.status || a.areaId !== original.areaId
					})
					.map((a) => a.fullName)
					.join(', ')}
				. Do you want to override the entered attendance?
			</ConfirmDialog>
		</AttendanceShell>
	)
}
